import { Component, inject, Input, input, OnDestroy, OnInit, output, ViewChild } from '@angular/core';
import {
  EndUser,
  Global360View,
  LineButton,
  Phone,
  Voicemail,
  VoicemailAlternateExtension,
  VoicemailFieldConfig,
  VoicemailRef,
} from '../../../shared/models/generated/ziroModels';
import { ActivatedRoute } from '@angular/router';
import { get } from 'lodash';
import { combineLatest, Subscription } from 'rxjs';
import { EndUserResource } from '../../../shared/resources/end-user.resource';
import { Global360ViewContext } from '../../../shared/contexts/global-360-view.context';
import { CurrentClusterContext } from '../../../shared/contexts/current-cluster.context';
import { VoicemailFormComponent, VoicemailFormEntity } from './form/voicemail-form.component';
import { ButtonStyles } from '../../../button/button.component';
import { SiteSummaryContext } from '../../../shared/contexts/site-summary.context';
import { PhoneResource } from '../../../shared/resources/phone.resource';
import { VoicemailResource } from '../../../shared/resources/voicemail.resource';
import { VoicemailAlternateExtensionsResource } from '../../../shared/resources/voicemail-alternate-extensions.resource';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { VoicemailFieldsConfigResource } from '../../../shared/resources/voicemail-fields-config.resource';
import { take } from 'rxjs/operators';
import { VoicemailSmtpNotificationsDeviceResource } from '../../../shared/resources/voicemail-smtp-notifications-device.resource';

@Component({
  selector: 'ziro-voicemail',
  templateUrl: './voicemail.component.html',
  standalone: false,
})
export class VoicemailComponent implements OnInit, OnDestroy {
  get setEntitySourceFromVoicemail(): () => void {
    return this._setEntitySourceFromVoicemail;
  }

  set setEntitySourceFromVoicemail(value: () => void) {
    this._setEntitySourceFromVoicemail = value;
  }
  private route = inject(ActivatedRoute);
  private endUserResource = inject(EndUserResource);
  private global360ViewContext = inject(Global360ViewContext);
  private currentClusterContext = inject(CurrentClusterContext);
  private voicemailFieldsConfigResource = inject(VoicemailFieldsConfigResource);
  private siteSummaryContext = inject(SiteSummaryContext);
  private phoneResource = inject(PhoneResource);
  private voicemailResource = inject(VoicemailResource);
  private voicemailAlternateExtensionsResource = inject(VoicemailAlternateExtensionsResource);
  private voicemailSmtpNotificationsDeviceResource = inject(VoicemailSmtpNotificationsDeviceResource);

  @ViewChild(VoicemailFormComponent) voicemailForm: VoicemailFormComponent;
  readonly modalProperties? = input<any>();
  // TODO: Skipped for migration because:
  //  Your application code writes to the input. This prevents migration.
  @Input() username?: string;
  readonly siteId = input<number>();
  readonly voicemailSaved = output<boolean | string>();
  readonly voicemailDeleted = output<boolean | string>();
  readonly voicemailRefUpdated = output<VoicemailRef>();
  readonly showLoading = output<boolean>();
  extension: string;
  phone: Phone;
  endUser: EndUser;
  buttonStyles = ButtonStyles;
  ziroIcons = ZiroIcons;
  originalValue = {
    alias: '',
    alternateExtensions: [],
    displayName: '',
    firstName: '',
    lastName: '',
    voicemailTemplate: '',
    smtpNotificationDevice: '',
  } as VoicemailFormEntity;

  unityServerId: number;
  serverId: number;
  isExistingVoicemail: boolean;
  voicemail: Voicemail;
  alternateExtensions: VoicemailAlternateExtension[] = [];
  voicemailId: string;
  phoneId: string;
  line: string;
  isLoading = true;
  voicemailFieldConfig: VoicemailFieldConfig;
  isNewVoicemailOnSharedLine: boolean;
  isExistingVoicemailOnSharedLine: boolean;
  isNewVoicemailOnPrimaryExtension: boolean;
  isExistingVoicemailOnPrimaryExtension: boolean;
  isPending = false;
  isDeleting = false;
  voicemailRef: VoicemailRef;
  isPublicPhone = false;
  isExistingVoicemailOnPublicPhone: boolean;
  global360View: Global360View;

  private _smtpNotificationDeviceValue: string;
  private _subscriptions = new Subscription();

  ngOnInit(): void {
    if (this.modalProperties()) {
      this.username = this.modalProperties().username;
      this.isPublicPhone = this.modalProperties().phone.owner === null;
    } else if (!this.username) {
      this.username = get(this.route, 'snapshot.params.username');
    }
    this.voicemailId = this.modalProperties()
      ? this.modalProperties().voicemailId
      : get(this.route, 'snapshot.params.voicemailId');
    this.phoneId = get(this.route, 'snapshot.params.phoneId');

    this.isNewVoicemailOnPrimaryExtension = !!(this.username && !this.phoneId && !this.voicemailId);
    this.isExistingVoicemailOnPrimaryExtension = !!(this.username && !this.phoneId && this.voicemailId);
    this.isNewVoicemailOnSharedLine = !!(!this.username && this.phoneId && !this.voicemailId);
    this.isExistingVoicemailOnSharedLine = !!(!this.username && this.phoneId && this.voicemailId);
    this.isExistingVoicemailOnPublicPhone = this.isPublicPhone && !!this.voicemailId;

    this.isExistingVoicemail = !!this.voicemailId;
    if (this.username) {
      this.global360ViewContext.init(this.username);
      this.global360ViewContext.state$.pipe(take(1)).subscribe((global360View) => {
        this.global360View = global360View;
      });
    }

    const loadingSub = combineLatest([this.currentClusterContext.state$, this.siteSummaryContext.state$]).subscribe(
      ([currentCluster, siteSummary]) => {
        this.unityServerId = this.siteSummaryContext.getSiteFromId(siteSummary, Number(this.siteId())).unityServerId;
        this.serverId = currentCluster.cucmServerId;
        if (this.modalProperties()) {
          this.extension = this.modalProperties().button.dn.extension;
        }
        if (this.isNewVoicemailOnPrimaryExtension || this.isExistingVoicemailOnPrimaryExtension) {
          this._getFieldConfigsFromPrimaryExtension();
        } else if (this.isNewVoicemailOnSharedLine || this.isExistingVoicemailOnSharedLine) {
          this.getFieldConfigsFromSharedLine();
        } else if (this.isPublicPhone) {
          if (this.isExistingVoicemailOnPublicPhone) {
            this._getVoicemail();
          } else {
            this._setVoicemailFieldConfigs();
          }
        } else if (this.voicemailId) {
          this._getVoicemail();
        }
      },
    );

    this._subscriptions.add(loadingSub);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  onDeleteComplete($event: boolean | string): void {
    this.voicemailDeleted.emit($event);
  }

  onSaveComplete(event: string | boolean): void {
    this.voicemailSaved.emit(event);
  }

  onVoicemailRefUpdated(event: VoicemailRef): void {
    this.voicemailRefUpdated.emit(event);
    this.voicemailRef = event;
  }

  private _setVoicemailFieldConfigs(): void {
    this.voicemailFieldsConfigResource
      .post({
        extension: this.modalProperties() ? this.modalProperties().dnRef.extension : this.extension,
        siteId: this.siteId(),
      })
      .subscribe((voicemailFieldConfig) => {
        this.voicemailFieldConfig = voicemailFieldConfig;
        this.originalValue.voicemailTemplate = voicemailFieldConfig.voicemailTemplate.defaultValue;
        if (
          this.modalProperties() &&
          voicemailFieldConfig.smtpNotificationDevice.defaultValue &&
          !this.originalValue.smtpNotificationDevice
        ) {
          this._smtpNotificationDeviceValue = voicemailFieldConfig.smtpNotificationDevice.defaultValue;
        }

        if (this.isExistingVoicemail || this.isExistingVoicemailOnPublicPhone) {
          this._setEntitySourceFromVoicemail();
        } else {
          this._setEntitySourceFromFieldConfigsDefault();
        }

        this.isLoading = false;
        this.showLoading.emit(this.isLoading);
      });
  }

  private _getFieldConfigsFromPrimaryExtension(): void {
    const global360ViewUpdate = this.global360ViewContext.state$.pipe(take(1)).subscribe((global360View) => {
      const global360User = this.global360ViewContext.getCurrentEnduser(global360View, this.serverId);
      this.endUserResource.get(global360User.ref.id, this.serverId).subscribe((endUser: EndUser) => {
        this.endUser = endUser;
        this.extension = this.endUser.primaryExtension.extension;

        if (this.isExistingVoicemailOnPrimaryExtension) {
          this._getVoicemail();
        } else {
          this._setVoicemailFieldConfigs();
        }
      });
    });
    this._subscriptions.add(global360ViewUpdate);
  }

  private getFieldConfigsFromSharedLine(): void {
    this.phoneResource.get(this.phoneId, this.serverId.toString()).subscribe((phone: Phone) => {
      this.phone = phone;

      const lineButton = phone.buttons.find((button: LineButton) => {
        return button && button.dn && button.dn.id === this.line;
      }) as LineButton;

      this.extension = lineButton?.dn.extension;

      if (this.isExistingVoicemailOnSharedLine) {
        this._getVoicemail();
      } else {
        this._setVoicemailFieldConfigs();
      }
    });
  }

  private _getVoicemail: () => void = () => {
    this.voicemailResource.get(this.unityServerId, this.voicemailId).subscribe((voicemail: Voicemail) => {
      this.voicemail = voicemail;
      this.voicemailId = voicemail.id;

      combineLatest([
        this.voicemailAlternateExtensionsResource.getAll(this.unityServerId, this.voicemailId),
        this.voicemailSmtpNotificationsDeviceResource.get(this.unityServerId, this.voicemailId),
      ]).subscribe(([voicemailAlternateExtensions, voicemailSmtpNotificationsDevice]) => {
        this.alternateExtensions = voicemailAlternateExtensions;
        this._smtpNotificationDeviceValue = voicemailSmtpNotificationsDevice.email;
        this._setVoicemailFieldConfigs();
      });
    });
  };

  private _setEntitySourceFromVoicemail: () => void = () => {
    this.originalValue.lastName = this.voicemail.lastName;
    this.originalValue.firstName = this.voicemail.firstName;
    this.originalValue.alias = this.voicemail.alias;
    this.originalValue.displayName = this.voicemail.displayName;
    this.originalValue.smtpNotificationDevice = this._smtpNotificationDeviceValue;
    this.alternateExtensions.forEach((extension) => {
      this.originalValue.alternateExtensions.push(extension.extension);
    });
  };

  private _setEntitySourceFromFieldConfigsDefault: () => void = () => {
    this.originalValue.lastName = this.voicemailFieldConfig.lastName.defaultValue;
    this.originalValue.firstName = this.voicemailFieldConfig.firstName.defaultValue;
    this.originalValue.alias = this.voicemailFieldConfig.alias.defaultValue;
    this.originalValue.displayName = this.voicemailFieldConfig.displayName.defaultValue;
    this.originalValue.smtpNotificationDevice = this.voicemailFieldConfig.smtpNotificationDevice.defaultValue;
    this.originalValue.alternateExtensions = this.voicemailFieldConfig.alternateExtension.defaultValue
      ? [this.voicemailFieldConfig.alternateExtension.defaultValue]
      : [];
  };
}
